import { QueryKey } from '@tanstack/react-query'

/**
 * Single source of truth for every react-query cache key in the webapp.
 *
 * react-query cache identity depends on the EXACT key tuple (same strings, same
 * order, same elements). Every builder here reproduces the literal tuple that the
 * authoritative query hook used before this module existed — do not reorder, retype,
 * or coerce the elements, or caching/invalidation silently drifts.
 *
 * Builders accept `string | undefined` where the original literal could carry an
 * undefined trip id (the query hooks guard with `enabled`), so the produced tuple
 * stays byte-identical to the previous inline array.
 */
export const queryKeys = {
	// Trips
	trips: (): QueryKey => ['trips'],
	trip: (tripId: string | undefined): QueryKey => ['trip', tripId],
	tripMembers: (tripId: string | undefined): QueryKey => ['tripMembers', tripId],
	tripDayInfos: (tripId: string | undefined): QueryKey => ['tripDayInfos', tripId],

	// Trip resources
	events: (tripId: string | undefined): QueryKey => ['events', tripId],
	expenses: (tripId: string | undefined): QueryKey => ['expenses', tripId],
	paymentsOwed: (tripId: string | undefined): QueryKey => ['paymentsOwed', tripId],
	payups: (tripId: string | undefined): QueryKey => ['payups', tripId],
	payup: (tripId: string | undefined, payUpId: number | undefined): QueryKey => [
		'payup',
		tripId,
		payUpId,
	],
	flights: (tripId: string | undefined): QueryKey => ['flights', tripId],
	accommodations: (tripId: string | undefined): QueryKey => ['accommodations', tripId],
	ideas: (tripId: string | undefined): QueryKey => ['ideas', tripId],
	travelSegments: (tripId: string | undefined): QueryKey => ['travel-segments', tripId],
	files: (tripId: string | undefined): QueryKey => ['files', tripId],

	/**
	 * Composite group: every cache key whose data changes when itinerary content
	 * changes (events, the travel segments between them, and per-day info). The
	 * realtime `itinerary_updated` handler and the two import-completion paths all
	 * invalidate exactly this trio — named once here so they cannot drift apart.
	 */
	itineraryContentKeys: (tripId: string | undefined): QueryKey[] => [
		queryKeys.events(tripId),
		queryKeys.travelSegments(tripId),
		queryKeys.tripDayInfos(tripId),
	],

	// Itinerary import
	itineraryImport: (
		tripId: string | undefined,
		importId: string | undefined,
	): QueryKey => ['itinerary-import', tripId, importId],
	itineraryImports: (tripId: string | undefined): QueryKey => ['itinerary-imports', tripId],
	publicItinerary: (tripCode: string | undefined): QueryKey => ['public-itinerary', tripCode],

	// User / auth
	me: (): QueryKey => ['me'],
	qrToken: (): QueryKey => ['qr-token'],
	qrTokenCheck: (token: string | undefined): QueryKey => ['qr-token-check', token],

	// Admin
	adminOverview: (from: string, to: string, includeInternal: boolean): QueryKey => [
		'admin',
		'overview',
		from,
		to,
		includeInternal,
	],
	adminUser: (id: number): QueryKey => ['admin', 'user', id],
	adminUsers: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'users', from, to, page, pageSize, q, includeInternal],
	adminTrip: (id: number): QueryKey => ['admin', 'trip', id],
	adminTrips: (
		from: string,
		to: string,
		page: number,
		pageSize: number,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'trips', from, to, page, pageSize, q, includeInternal],
	adminEvent: (id: number): QueryKey => ['admin', 'event', id],
	adminEvents: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'events', from, to, page, pageSize, q, includeInternal],
	adminExpense: (id: number): QueryKey => ['admin', 'expense', id],
	adminExpenses: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'expenses', 'list', from, to, page, pageSize, q, includeInternal],
	adminIdea: (id: number): QueryKey => ['admin', 'idea', id],
	adminIdeas: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'ideas', from, to, page, pageSize, q, includeInternal],
	adminPayups: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'payups', 'list', from, to, page, pageSize, includeInternal],
}
